/* eslint-disable max-len */
import {
    MutableRefObject,
    useCallback,
    useMemo,
    useState,
} from 'react'
import Box from '@mui/material/Box'
import {
    GridCallbackDetails,
    useGridApiRef,
    GridRowSelectionModel, GridSlotProps,
    GridApiPro, GridPaginationModel,
} from '@mui/x-data-grid-pro'

import merge from 'lodash/merge'

import SkyBaseDataGrid, {
    ExtendedGridApiPro, SkyBaseDataSourceProvider, SkyDataGridColumnsState, SkyDataGridLocalStorage,
} from 'app/PoC/DataGridDemoPages/components/SkyBaseDataGrid'
import {
    SkyNetPaginationMode,
} from 'app/shared-components/SkyNetPagination'
import usePaginationStyles from 'app/tss/pagination.styles'
import {
    DataGridSupportedFeatures,
} from 'app/PoC/DataGridDemoPages/components/SkyNetMasterDataGrid/SkyNetMasterDataGrid.types'
import {
    ColumnConfigMappingType,
} from 'app/PoC/DataGridDemoPages/components/SkyBaseDataGrid/SkyBaseDataGrid.types'
import {
    PAGE_SIZE_DEFAULT,
} from 'app/PoC/DataGridDemoPages/components/SkyBaseDataGrid/constants'
import useDataGridColumns from 'app/PoC/DataGridDemoPages/components/hooks/useDataGridColumns'
import {
    ActionComponentsKey,
} from '../../SkyNetObjectRoutes/SkyNetObjectRoutes.types'
import SkyNetResizablePanel from '../SkyNetResizablePanel'

import {
    defaultSetColumnsStateConverter,
    defaultInitColumnsStateConverter,
} from '../SkyBaseDataGrid/hooks/usePersistedColumnsState'
import {
    useVisibleColumnsWithDetailsView,
} from '../hooks/useVisibiltyModel'
import useGetExtendedFilterParams from '../hooks/useGetExtendedFilterParams'
import useSkyNetObjectProfileContext from '../SkyNetObjectProfile/SkyNetObjectProfileContext'
import useStyles from './SkyNetMasterDataGrid.styles'
import SkyNetDataGridToolbar from './components/SkyNetDataGridToolbar'
import SkyNetMasterDataGridPagination from './components/SkyNetMasterDataGridPagination'

type SkyNetDataGridProps = {
    persistentKey: string,
    columns: ColumnConfigMappingType[],
    filtersUnrelatedToColumns?: ColumnConfigMappingType[],
    customDataSource: SkyBaseDataSourceProvider,
    rowHeight?: number | 'auto',
    height?: string | number,
    toolbarControls?: DataGridSupportedFeatures[],
    tabsComponent: JSX.Element,
    onRowClick?: (params: any) => void,
    rowSelectionModel?: GridRowSelectionModel,
    onRowSelectionModelChange?: (newRowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails) => void,
    isDetailsView?: boolean,
    initialTableWidthPercent?: number,
    toolbarActions?: ActionComponentsKey[],
    gridApiRef?: MutableRefObject<GridApiPro>,
}

export default function SkyNetMasterDataGrid({
    persistentKey,
    columns: defColumns,
    customDataSource,
    filtersUnrelatedToColumns,
    height,
    isDetailsView,
    onRowClick,
    onRowSelectionModelChange,
    rowHeight = 'auto',
    rowSelectionModel,
    tabsComponent,
    toolbarActions,
    toolbarControls,
    gridApiRef,
}: Readonly<SkyNetDataGridProps>) {
    const newApiRef = useGridApiRef<ExtendedGridApiPro>()
    const apiRef: MutableRefObject<ExtendedGridApiPro> = gridApiRef || newApiRef
    const {
        cx,
        classes: paginationStyles,
    } = usePaginationStyles()
    const {
        classes,
    } = useStyles({
        isDetailsView,
    })
    // this is used to set the position of filter panel and column menu when in details view
    const [
        panelTargetEl,
        setPanelTargetEl,
    ] = useState<HTMLButtonElement | null>(null)

    const columnsWithLabels = useDataGridColumns({
        columnsDef: defColumns, filtersUnrelatedToColumns, customDataSource,
    })

    const {
        visibilityModel,
        onColumnVisibilityModelChange,
        tableWidthPercent,
        setTableWidthPercent,
    } = useVisibleColumnsWithDetailsView({
        columns: columnsWithLabels,
        persistentKey,
        isDetailsView,
    })

    const activeToolbarControls = useMemo(() => {
        /* For custom toolbarControls */
        const controlsMap: Partial<Record<DataGridSupportedFeatures, JSX.Element>> = {}

        return Object.entries(controlsMap).reduce((acc, [
            controlType,
            element,
        ]) => {
            if (toolbarControls?.includes(controlType as DataGridSupportedFeatures)) {
                acc.push(element)
            }
            return acc
        }, [] as JSX.Element[])
    }, [toolbarControls])

    const getFilterParams = useGetExtendedFilterParams({
        requestData: customDataSource.request.data,
        gridApiRef: apiRef,
    })

    const CustomToolbar = useCallback(() => {
        const filterableColumns = columnsWithLabels.filter((col) => {
            return col.filterable
        }) || []

        return (
            <SkyNetDataGridToolbar
                toolbarControls={activeToolbarControls}
                showFilterButton={Boolean(filterableColumns.length) || Boolean(filtersUnrelatedToColumns?.length)}
                tabsComponent={tabsComponent}
                isDetailsView={isDetailsView}
                setPanelTargetEl={setPanelTargetEl}
                toolbarActions={toolbarActions || []}
                toolbarActionsProps={{
                    getFilterParams,
                }}
            />
        )
    }, [
        columnsWithLabels,
        activeToolbarControls,
        filtersUnrelatedToColumns?.length,
        tabsComponent,
        isDetailsView,
        toolbarActions,
        getFilterParams,
    ])

    const CustomPagination = useCallback(() => {
        return (
            <div className={cx(paginationStyles.paginationContainer, paginationStyles.withoutPadding)}>
                <SkyNetMasterDataGridPagination
                    mode={isDetailsView ? SkyNetPaginationMode.SIMPLE : undefined}
                    useCard={isDetailsView ? false : undefined}
                />
            </div>
        )
    }, [
        cx,
        isDetailsView,
        paginationStyles.paginationContainer,
        paginationStyles.withoutPadding,
    ])

    const slotProps = useMemo(() => {
        return isDetailsView ? {
            panel: {
                target: panelTargetEl,
            },
        } as unknown as GridSlotProps : undefined
    }, [
        isDetailsView,
        panelTargetEl,
    ])

    const customColumnsSetStateConverter = useCallback((value: SkyDataGridColumnsState, oldState: SkyDataGridLocalStorage) => {
        if (isDetailsView) {
            return {
                detailsView: merge({}, oldState?.detailsView || {}, defaultSetColumnsStateConverter(value)),
            }
        }
        return defaultSetColumnsStateConverter(value)
    }, [isDetailsView])

    const customColumnsInitStateConverter = useCallback((dataGridState: SkyDataGridLocalStorage) => {
        return isDetailsView ? (dataGridState?.detailsView?.columns ?? defaultInitColumnsStateConverter(dataGridState))
            : defaultInitColumnsStateConverter(dataGridState)
    }, [isDetailsView])

    const {
        page,
        setPage,
    } = useSkyNetObjectProfileContext()

    const paginationModel = useMemo(() => {
        return {
            pageSize: PAGE_SIZE_DEFAULT,
            page,
        }
    }, [page])

    const onPaginationModelChange = useCallback((model: GridPaginationModel) => {
        setPage(model.page)
    }, [setPage])

    const dataGridElement = useMemo(() => {
        return (
            <SkyBaseDataGrid
                persistentKey={persistentKey}
                rowHeight={rowHeight}
                onRowClick={onRowClick}
                CustomToolbar={CustomToolbar}
                gridApiRef={apiRef}
                columns={columnsWithLabels}
                customDataSource={customDataSource}
                height={height}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={onRowSelectionModelChange}
                Pagination={CustomPagination}
                slotProps={slotProps}
                classNames={{
                    container: classes.dataGridContainer,
                }}
                visibilityModel={visibilityModel}
                onColumnVisibilityModelChange={onColumnVisibilityModelChange}
                customColumnsSetStateConverter={customColumnsSetStateConverter}
                customColumnsInitStateConverter={customColumnsInitStateConverter}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
            />
        )
    }, [
        persistentKey,
        CustomToolbar,
        CustomPagination,
        classes.dataGridContainer,
        columnsWithLabels,
        customDataSource,
        apiRef,
        height,
        onColumnVisibilityModelChange,
        onRowClick,
        onRowSelectionModelChange,
        rowHeight,
        rowSelectionModel,
        slotProps,
        visibilityModel,
        customColumnsSetStateConverter,
        customColumnsInitStateConverter,
        paginationModel,
        onPaginationModelChange,
    ])

    return (
        isDetailsView ? (
            <SkyNetResizablePanel
                initialWidthPercent={tableWidthPercent}
                onWidthChange={setTableWidthPercent}
            >
                {dataGridElement}
            </SkyNetResizablePanel>
        ) : (
            <Box className={classes.gridWrapper}>
                {dataGridElement}
            </Box>
        )
    )
}
